import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  FileText,
  Download,
  RefreshCw,
  Calendar,
  Users,
  AlertCircle,
  CheckCircle,
  Clock,
  Play,
  Loader2,
  Eye,
  Send
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  // New workflow
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: Clock },
  ready: { label: 'Ready', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  published: { label: 'Published', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  // Legacy — kept for pre-migration certs
  available: { label: 'Published', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  processing: { label: 'Processing', color: 'bg-amber-100 text-amber-700', icon: Clock },
  not_due: { label: 'Not Due', color: 'bg-gray-100 text-gray-600', icon: Clock }
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2
  }).format(value || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export default function AdminTaxCertificatesPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  // State
  const [loading, setLoading] = useState(true);
  const [certificates, setCertificates] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [activeTab, setActiveTab] = useState('certificates');

  // Filters
  const [yearFilter, setYearFilter] = useState('');
  const [investorFilter, setInvestorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Selection (for bulk publish)
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Generate dialog
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [generateScope, setGenerateScope] = useState('all'); // 'all' | 'single'
  const [generateInvestorId, setGenerateInvestorId] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generationResult, setGenerationResult] = useState(null);
  const [taxTemplates, setTaxTemplates] = useState([]);
  const [generateTemplateId, setGenerateTemplateId] = useState('');
  const [includeZero, setIncludeZero] = useState(false);

  // Publish dialog
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [publishNotify, setPublishNotify] = useState(true);
  const [publishing, setPublishing] = useState(false);

  // Preview dialog
  const [previewCert, setPreviewCert] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');

  // Get available tax years (current year and previous 3 years)
  const currentYear = new Date().getFullYear();
  const taxYears = [
    currentYear.toString(),
    (currentYear - 1).toString(),
    (currentYear - 2).toString(),
    (currentYear - 3).toString()
  ];

  // Fetch certificates
  const fetchCertificates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (yearFilter) params.append('tax_year', yearFilter);
      if (investorFilter) params.append('user_id', investorFilter);
      if (statusFilter) params.append('status', statusFilter);

      const response = await axios.get(`${API_URL}/api/admin/tax/certificates?${params}`, { headers });
      setCertificates(response.data || []);
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Error fetching certificates:', error);
      toast.error('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  }, [token, yearFilter, investorFilter, statusFilter]);

  // Fetch investors — pull ALL (large limit) so PTAM + PTC + Trust
  // investors all show in the picker. Backend defaults to limit=20.
  const fetchInvestors = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/investors?limit=2000`,
        { headers }
      );
      const investorData = response.data.items || response.data || [];
      setInvestors(Array.isArray(investorData) ? investorData : []);
    } catch (error) {
      console.error('Error fetching investors:', error);
      setInvestors([]);
    }
  }, [token]);

  // Fetch tax certificate templates
  const fetchTaxTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/tax/templates`, { headers });
      const tpls = response.data?.templates || [];
      setTaxTemplates(tpls);
      // Default select the flagged default template
      const def = tpls.find(t => t.is_default) || tpls[0];
      if (def) setGenerateTemplateId(def.id);
    } catch (error) {
      console.error('Error fetching tax templates:', error);
      setTaxTemplates([]);
    }
  }, [token]);

  useEffect(() => {
    fetchCertificates();
    fetchInvestors();
    fetchTaxTemplates();
  }, [fetchCertificates, fetchInvestors, fetchTaxTemplates]);

  // Generate certificates — single or bulk
  const handleGenerateBatch = async () => {
    if (!selectedYear) {
      toast.error('Please select a tax year');
      return;
    }
    if (generateScope === 'single' && !generateInvestorId) {
      toast.error('Please pick an investor');
      return;
    }

    setGenerating(true);
    setGenerationResult(null);
    try {
      const body = {
        tax_year: selectedYear,
        user_ids: generateScope === 'single' ? [generateInvestorId] : null,
        template_id: generateTemplateId || null,
        include_zero: includeZero,
      };
      const response = await axios.post(
        `${API_URL}/api/admin/tax/certificates/generate`,
        body,
        { headers }
      );
      setGenerationResult(response.data);
      toast.success(
        `Generated ${response.data.generated} draft${response.data.generated === 1 ? '' : 's'} ` +
        `(${response.data.skipped} skipped, ${response.data.replaced} replaced)`
      );
      fetchCertificates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate certificates');
    } finally {
      setGenerating(false);
    }
  };

  // Preview a certificate — fetch the rendered HTML
  const handlePreview = async (cert) => {
    setPreviewCert(cert);
    setPreviewHtml('');
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/tax/certificates/${cert.id}/preview?format=html`,
        { headers, responseType: 'text' }
      );
      setPreviewHtml(response.data);
    } catch (error) {
      toast.error('Failed to load preview');
      setPreviewCert(null);
    }
  };

  // Publish selected certificates
  const handlePublish = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      toast.error('No certificates selected');
      return;
    }
    setPublishing(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/tax/certificates/publish`,
        { ids, notify: publishNotify },
        { headers }
      );
      toast.success(
        `Published ${response.data.published} certificate${response.data.published === 1 ? '' : 's'}` +
        (response.data.notified > 0 ? ` — ${response.data.notified} notified` : '')
      );
      setSelectedIds(new Set());
      setShowPublishDialog(false);
      fetchCertificates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  // Mark a single cert as ready
  const handleMarkReady = async (cert) => {
    try {
      await axios.post(
        `${API_URL}/api/admin/tax/certificates/${cert.id}/mark-ready`,
        {},
        { headers }
      );
      toast.success('Marked ready');
      fetchCertificates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark ready');
    }
  };

  // Toggle row selection
  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };
  const selectableCerts = certificates.filter(c => c.status === 'draft' || c.status === 'ready');
  const allSelectableSelected = selectableCerts.length > 0 &&
    selectableCerts.every(c => selectedIds.has(c.id));
  const toggleSelectAll = () => {
    if (allSelectableSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableCerts.map(c => c.id)));
    }
  };

  // Group certificates by tax year for summary
  const certsByYear = certificates.reduce((acc, cert) => {
    const year = cert.tax_year;
    if (!acc[year]) {
      acc[year] = { total: 0, draft: 0, ready: 0, published: 0, totalGross: 0, totalTax: 0 };
    }
    acc[year].total++;
    const st = cert.status === 'available' ? 'published' : cert.status;
    if (st === 'draft') acc[year].draft++;
    if (st === 'ready') acc[year].ready++;
    if (st === 'published') acc[year].published++;
    acc[year].totalGross += cert.gross_interest || 0;
    acc[year].totalTax += cert.tax_withheld || 0;
    return acc;
  }, {});

  if (loading && certificates.length === 0) {
    return (
      <div className="space-y-6" data-testid="tax-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-tax-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="h-6 w-6" />
            Tax Certificate Management
          </h1>
          <p className="text-gray-500 mt-1">
            Generate and manage IT3(b) certificates for investors
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              fetchCertificates();
              toast.success('Refreshed');
            }}
            data-testid="refresh-tax-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {selectedIds.size > 0 && (
            <Button
              onClick={() => setShowPublishDialog(true)}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="publish-selected-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              Publish Selected ({selectedIds.size})
            </Button>
          )}
          <Button
            onClick={() => setShowGenerateDialog(true)}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900"
            data-testid="generate-batch-btn"
          >
            <Play className="h-4 w-4 mr-2" />
            Generate Certificates
          </Button>
        </div>
      </div>

      {/* Summary by Year */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {taxYears.slice(0, 4).map(year => {
          const stats = certsByYear[year] || { total: 0, available: 0, processing: 0, totalGross: 0, totalTax: 0 };
          return (
            <Card key={year}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">{year} Tax Year</span>
                  <Calendar className="h-4 w-4 text-gray-400" />
                </div>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                <div className="flex gap-2 mt-1 flex-wrap text-xs">
                  {stats.draft > 0 && <span className="text-gray-600">{stats.draft} draft</span>}
                  {stats.ready > 0 && <span className="text-blue-600">{stats.ready} ready</span>}
                  {stats.published > 0 && <span className="text-green-600">{stats.published} published</span>}
                </div>
                {stats.totalGross > 0 && (
                  <p className="text-xs text-gray-500 mt-2">
                    Total WHT: {formatCurrency(stats.totalTax)}
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="certificates">All Certificates</TabsTrigger>
          <TabsTrigger value="investors">By Investor</TabsTrigger>
        </TabsList>

        <TabsContent value="certificates" className="mt-4">
          {/* Filters */}
          <Card className="mb-4">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="w-40">
                  <Label className="text-xs text-gray-500">Tax Year</Label>
                  <Select value={yearFilter || "all"} onValueChange={(v) => setYearFilter(v === "all" ? "" : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Years" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Years</SelectItem>
                      {taxYears.map(year => (
                        <SelectItem key={year} value={year}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-64">
                  <Label className="text-xs text-gray-500">Investor</Label>
                  <Select value={investorFilter || "all"} onValueChange={(v) => setInvestorFilter(v === "all" ? "" : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Investors" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Investors</SelectItem>
                      {investors.map(inv => (
                        <SelectItem key={inv.id} value={inv.id}>
                          {inv.first_name} {inv.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-40">
                  <Label className="text-xs text-gray-500">Status</Label>
                  <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="tax-status-filter">
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="ready">Ready</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" onClick={() => {
                  setYearFilter('');
                  setInvestorFilter('');
                  setStatusFilter('');
                }}>
                  Clear
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Certificates Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="w-8">
                      <input
                        type="checkbox"
                        checked={allSelectableSelected}
                        onChange={toggleSelectAll}
                        data-testid="select-all-tax"
                        aria-label="Select all"
                      />
                    </TableHead>
                    <TableHead>Tax Year</TableHead>
                    <TableHead>Investor</TableHead>
                    <TableHead>Certificate #</TableHead>
                    <TableHead className="text-right">Gross Interest</TableHead>
                    <TableHead className="text-right">WHT</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Generated</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {certificates.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-12 text-gray-500">
                        No certificates found. Click <strong>Generate Certificates</strong> to create drafts for a tax year.
                      </TableCell>
                    </TableRow>
                  ) : (
                    certificates.map(cert => {
                      const normalizedStatus = cert.status === 'available' ? 'published' : cert.status;
                      const statusConfig = STATUS_CONFIG[normalizedStatus] || STATUS_CONFIG.draft;
                      const StatusIcon = statusConfig.icon;
                      const isSelectable = normalizedStatus === 'draft' || normalizedStatus === 'ready';
                      const isChecked = selectedIds.has(cert.id);
                      return (
                        <TableRow key={cert.id} data-testid={`tax-row-${cert.id}`}>
                          <TableCell>
                            {isSelectable && (
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => toggleSelect(cert.id)}
                                data-testid={`select-tax-${cert.id}`}
                                aria-label="Select certificate"
                              />
                            )}
                          </TableCell>
                          <TableCell className="font-medium">{cert.tax_year}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">
                                {cert.investor_name || `${cert.user?.first_name || ''} ${cert.user?.last_name || ''}`.trim()}
                              </p>
                              <p className="text-xs text-gray-500">{cert.user?.email}</p>
                            </div>
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {cert.certificate_number || '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(cert.gross_interest)}
                          </TableCell>
                          <TableCell className="text-right text-red-600">
                            {formatCurrency(cert.tax_withheld)}
                          </TableCell>
                          <TableCell>
                            <Badge className={statusConfig.color}>
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-gray-500">
                            {formatDate(cert.generated_at || cert.generated_date)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handlePreview(cert)}
                                data-testid={`preview-tax-${cert.id}`}
                              >
                                <Eye className="h-3 w-3 mr-1" />
                                Preview
                              </Button>
                              {normalizedStatus === 'draft' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleMarkReady(cert)}
                                  data-testid={`mark-ready-${cert.id}`}
                                >
                                  Mark Ready
                                </Button>
                              )}
                              {isSelectable && (
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  onClick={() => {
                                    setSelectedIds(new Set([cert.id]));
                                    setShowPublishDialog(true);
                                  }}
                                  data-testid={`publish-tax-${cert.id}`}
                                >
                                  <Send className="h-3 w-3 mr-1" />
                                  Publish
                                </Button>
                              )}
                              {normalizedStatus === 'published' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  data-testid={`download-tax-${cert.id}`}
                                  onClick={async () => {
                                    try {
                                      const res = await axios.get(
                                        `${API_URL}/api/admin/tax/certificates/${cert.id}/preview?format=pdf`,
                                        { headers, responseType: 'blob' }
                                      );
                                      const blob = new Blob([res.data], { type: 'application/pdf' });
                                      const url = window.URL.createObjectURL(blob);
                                      const link = document.createElement('a');
                                      link.href = url;
                                      link.setAttribute('download', cert.file_name || 'certificate.pdf');
                                      document.body.appendChild(link);
                                      link.click();
                                      link.remove();
                                      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
                                    } catch (err) {
                                      toast.error(err.response?.status === 503
                                        ? 'PDF renderer not available in this environment'
                                        : 'Failed to download PDF');
                                    }
                                  }}
                                >
                                  <Download className="h-3 w-3 mr-1" />
                                  Download
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="investors" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Certificates by Investor</CardTitle>
              <CardDescription>View certificate status for each investor</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Investor</TableHead>
                    <TableHead>Email</TableHead>
                    {taxYears.slice(0, 3).map(year => (
                      <TableHead key={year} className="text-center">{year}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {investors.filter(i => i.role === 'investor').map(inv => {
                    const invCerts = certificates.filter(c => c.user_id === inv.id);
                    return (
                      <TableRow key={inv.id}>
                        <TableCell className="font-medium">
                          {inv.first_name} {inv.last_name}
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">{inv.email}</TableCell>
                        {taxYears.slice(0, 3).map(year => {
                          const cert = invCerts.find(c => c.tax_year === year);
                          return (
                            <TableCell key={year} className="text-center">
                              {cert ? (
                                <Badge className={STATUS_CONFIG[cert.status]?.color || 'bg-gray-100'}>
                                  {cert.status === 'available' ? (
                                    <CheckCircle className="h-3 w-3" />
                                  ) : (
                                    <Clock className="h-3 w-3" />
                                  )}
                                </Badge>
                              ) : (
                                <span className="text-gray-300">-</span>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Generate Certificates Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Generate IT3(b) Certificates
            </DialogTitle>
            <DialogDescription>
              Generate tax certificates for all investors who received distributions in the selected tax year
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Tax Year *</Label>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger data-testid="generate-year-select">
                  <SelectValue placeholder="Select year" />
                </SelectTrigger>
                <SelectContent>
                  {taxYears.map(year => (
                    <SelectItem key={year} value={year}>
                      March {year} - February {parseInt(year) + 1}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                South African tax year runs from 1 March to 28/29 February
              </p>
            </div>

            <div>
              <Label>Scope *</Label>
              <Select value={generateScope} onValueChange={setGenerateScope}>
                <SelectTrigger data-testid="generate-scope-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All eligible investors</SelectItem>
                  <SelectItem value="single">Single investor</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {generateScope === 'all'
                  ? 'Generates a draft for every active investor with paid distributions in the tax year.'
                  : 'Generates a draft for one selected investor.'}
              </p>
            </div>

            {generateScope === 'single' && (
              <div>
                <Label>Investor *</Label>
                <Select value={generateInvestorId} onValueChange={setGenerateInvestorId}>
                  <SelectTrigger data-testid="generate-investor-select">
                    <SelectValue placeholder="Pick an investor" />
                  </SelectTrigger>
                  <SelectContent>
                    {investors.filter(i => i.role === 'investor').map(inv => {
                  const label = inv.entity_name ||
                    `${inv.first_name || ''} ${inv.last_name || ''}`.trim() ||
                    inv.email;
                  const rp = inv.risk_participant_code ? ` [${inv.risk_participant_code}]` : '';
                  return (
                    <SelectItem key={inv.id} value={inv.id}>
                      {label}{rp} · {inv.email}
                    </SelectItem>
                  );
                })}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label>Template *</Label>
              <Select value={generateTemplateId} onValueChange={setGenerateTemplateId}>
                <SelectTrigger data-testid="generate-template-select">
                  <SelectValue placeholder={taxTemplates.length === 0 ? 'No templates found — fallback layout will be used' : 'Pick a template'} />
                </SelectTrigger>
                <SelectContent>
                  {taxTemplates.length === 0 && (
                    <SelectItem value="__none__" disabled>
                      No tax_certificate templates in the system
                    </SelectItem>
                  )}
                  {taxTemplates.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}{t.is_default ? ' (default)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Templates live in <code>/admin/templates</code> as
                type <code>tax_certificate</code>.
              </p>
            </div>

            <div className="flex items-start gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <input
                type="checkbox"
                id="include-zero"
                checked={includeZero}
                onChange={(e) => setIncludeZero(e.target.checked)}
                className="mt-1"
                data-testid="include-zero-checkbox"
              />
              <label htmlFor="include-zero" className="text-sm cursor-pointer">
                <span className="font-medium">Include investors with zero interest income</span>
                <p className="text-xs text-gray-500 mt-0.5">
                  By default investors with no interest earned in the year are skipped. Tick this to still generate a cert for them (e.g. for record-keeping).
                </p>
              </label>
            </div>

            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <AlertCircle className="h-4 w-4 inline mr-1" />
                Certificates are created as <strong>drafts</strong>. Investors will NOT see them until you review and publish.
              </p>
            </div>

            {generationResult && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm font-medium text-green-800 mb-2">
                  <CheckCircle className="h-4 w-4 inline mr-1" />
                  Generation Complete
                </p>
                <div className="text-sm text-green-700 space-y-1">
                  <p>Generated: {generationResult.generated} draft{generationResult.generated === 1 ? '' : 's'}</p>
                  <p>Replaced: {generationResult.replaced} (previous drafts overwritten)</p>
                  <p>Skipped: {generationResult.skipped} (no paid distributions)</p>
                  {generationResult.errors?.length > 0 && (
                    <p className="text-red-600">Errors: {generationResult.errors.length}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>
              Close
            </Button>
            <Button
              onClick={handleGenerateBatch}
              disabled={generating}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900"
            >
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Generate Certificates
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Preview Dialog */}
      <Dialog open={!!previewCert} onOpenChange={(open) => { if (!open) { setPreviewCert(null); setPreviewHtml(''); } }}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Preview — {previewCert?.certificate_number}
            </DialogTitle>
            <DialogDescription>
              {previewCert?.investor_name} · {previewCert?.tax_year_label || previewCert?.tax_year}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 border rounded-lg overflow-hidden bg-white">
            {previewHtml ? (
              <iframe
                title="Certificate preview"
                srcDoc={previewHtml}
                className="w-full h-full"
                data-testid="tax-preview-iframe"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const res = await axios.get(
                    `${API_URL}/api/admin/tax/certificates/${previewCert?.id}/preview?format=pdf`,
                    { headers, responseType: 'blob' }
                  );
                  const blob = new Blob([res.data], { type: 'application/pdf' });
                  const url = window.URL.createObjectURL(blob);
                  window.open(url, '_blank');
                  setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
                } catch (err) {
                  toast.error(err.response?.status === 503
                    ? 'PDF renderer not available in this environment'
                    : 'Failed to render PDF');
                }
              }}
              data-testid="preview-download-pdf-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
            <Button variant="outline" onClick={() => { setPreviewCert(null); setPreviewHtml(''); }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish Dialog */}
      <Dialog open={showPublishDialog} onOpenChange={setShowPublishDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-green-600" />
              Publish {selectedIds.size} Certificate{selectedIds.size === 1 ? '' : 's'}
            </DialogTitle>
            <DialogDescription>
              Publishing makes these certificates visible to investors in their portal.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={publishNotify}
                  onChange={(e) => setPublishNotify(e.target.checked)}
                  className="mt-1"
                  data-testid="publish-notify-checkbox"
                />
                <div>
                  <p className="text-sm font-medium text-blue-900">Notify investors</p>
                  <p className="text-xs text-blue-700">
                    Send an in-app notification and email to each investor (and any additional statement recipients configured on their profile).
                  </p>
                </div>
              </label>
            </div>
            <p className="text-xs text-gray-500">
              If unchecked, certificates become visible but no notification is sent — you can notify later by publishing again.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPublishDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handlePublish}
              disabled={publishing}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="confirm-publish-btn"
            >
              {publishing ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Publishing…</>
              ) : (
                <><Send className="h-4 w-4 mr-2" />Confirm Publish</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
